// frontend/src/components/esterilizacao/TelaEsterilizacao.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Box } from 'lucide-react';
import { subDays } from 'date-fns';

// Importação da Etiqueta (voltou um nível de pasta)
import Etiqueta from '../Etiqueta';

// Importação dos Componentes Filhos (mesma pasta)
import WizardTimeline from './WizardTimeline';
import WizardNav from './WizardNav';
import Step1Operacao from './Step1Operacao';
import Step2Pin from './Step2Pin';
import Step3Caixas from './Step3Caixas';
import Step4Resumo from './Step4Resumo';

export default function TelaEsterilizacao() {
  // Wizard
  const [step, setStep] = useState(1)
  const [operation, setOperation] = useState(null) // 'entrada' ou 'saida'

  // Dados principais
  const [codigo, setCodigo] = useState('')
  const [allCaixas, setAllCaixas] = useState([])
  const [caixas, setCaixas] = useState([])
  const [alunoPin, setAlunoPin] = useState('')
  const [alunoNome, setAlunoNome] = useState('')
  const [pinValidated, setPinValidated] = useState(false)
  const [alunoId, setAlunoId] = useState('')           // novo: armazena o ID do aluno
  const [alunoPeriodo, setAlunoPeriodo] = useState('')
  const [alunoTurno, setAlunoTurno] = useState('')
  const avancarButtonRef = useRef(null)
  const caixaInputRef = useRef(null)
  const [alunoCodEsterilizacao, setAlunoCodEsterilizacao] = useState('');
  const alunoPinInputRef = useRef(null)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const [preferSaidaVencidas, setPreferSaidaVencidas] = useState(false)
  const [orderedBoxIds, setOrderedBoxIds] = useState([]) // mantém ordem de primeira inserção dos ids





  // Histórico completo do aluno (para cálculo de estoque)
  const [fullHistory, setFullHistory] = useState([])
  const [estoqueRows, setEstoqueRows] = useState([])

  const [printData, setPrintData] = useState(null)
  const [printedIds, setPrintedIds] = useState([])

  const componentRef = useRef(null)
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}')
  const usuarioLogadoNome = storedUser.nome || ''
  const registrarButtonRef = useRef(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const BADGE_STYLES = [
    'bg-blue-100 text-blue-800',
    'bg-green-100 text-green-800',
    'bg-yellow-100 text-yellow-800',
    'bg-purple-100 text-purple-800',
  ]

  // 1) carrega todas as caixas para autocomplete
  useEffect(() => {
    axios.get('/api/caixas')
      .then(res => setAllCaixas(res.data))
      .catch(() => setAllCaixas([]))
  }, [])

  // 2) busca histórico do aluno por ID (evita homônimos)
  const fetchHistory = useCallback(() => {
    if (!pinValidated || !alunoId) return
    Promise.all([
      axios.get(`/api/movimentacoes/historico/${alunoId}`),
      axios.get(`/api/movimentacoes/estoque/${alunoId}`)
    ])
      .then(([hist, est]) => {
        setFullHistory(Array.isArray(hist.data) ? hist.data : [])
        setEstoqueRows(Array.isArray(est.data) ? est.data : [])
      })
      .catch(() => {
        setFullHistory([])
        setEstoqueRows([])
      })
  }, [alunoId, pinValidated])

  useEffect(fetchHistory, [fetchHistory])

  // Foca automaticamente o campo de PIN quando entra no passo 2
  useEffect(() => {
    if (step === 2) {
      setTimeout(() => alunoPinInputRef.current?.focus(), 0)
    }
  }, [step])

  // Foca o campo "Informar Caixas" ao entrar no passo 3
  useEffect(() => {
    if (step === 3) {
      setTimeout(() => caixaInputRef.current?.focus(), 0)
    }
  }, [step])

  // Foca o botão Registrar ao entrar no passo 4
  useEffect(() => {
    if (step === 4) {
      setTimeout(() => registrarButtonRef.current?.focus(), 0)
    }
  }, [step])

  // Atalhos: F3 para Entrada, F4 para Saída (apenas no passo 1)
  useEffect(() => {
    function onHotkey(e) {
      // Voltar (F1) e Avançar (F8)
      if (e.key === 'F1') {
        if (step > 1) {
          e.preventDefault()
          handleBack()
        }
        return
      }
      if (e.key === 'F8') {
        if (step > 1 && step < 4) {
          e.preventDefault()
          if (step === 2) {
            if (pinValidated) {
              setStep(3)
              setTimeout(() => caixaInputRef.current?.focus(), 0)
            } else {
              // sem PIN válido, apenas foca o campo PIN
              setTimeout(() => alunoPinInputRef.current?.focus(), 0)
            }
          } else if (step === 3) {
            setStep(4)
            setTimeout(() => registrarButtonRef.current?.focus(), 0)
          }
        }
        return
      }

      // Entrar na operação a partir do passo 1
      if (step !== 1) return
      if (e.key === 'F3') {
        e.preventDefault()
        setOperation('entrada'); setStep(2)
      } else if (e.key === 'F4') {
        e.preventDefault()
        setOperation('saida'); setStep(2)
      }
    }
    window.addEventListener('keydown', onHotkey)
    return () => window.removeEventListener('keydown', onHotkey)
  }, [step, pinValidated])

  // Reseta destaque de sugestão quando altera o texto
  useEffect(() => {
    setHighlightIndex(-1)
  }, [codigo])

  // autocomplete de caixas
  const suggestions = codigo
    ? allCaixas.filter(c =>
      c.nome.toLowerCase().includes(codigo.toLowerCase()) ||
      c.codigo_barras.includes(codigo)
    ).slice(0, 5)
    : []

  const addCaixa = c => {
    // registra ordem do id se for a primeira vez
    setOrderedBoxIds(prev => (prev.includes(c.id) ? prev : [...prev, c.id]))
    setCaixas(prev => [...prev, c])
    toast.success(`Caixa adicionada: ${c.nome}`, { autoClose: 5000 })
    setCodigo('')
    caixaInputRef.current?.focus()
  }

  const buscarCaixa = () => {
    if (!codigo.trim()) {
      toast.warn('Digite um código ou nome da caixa', { autoClose: 5000 })
      return
    }
    const encontrada = allCaixas.find(c =>
      c.codigo_barras === codigo ||
      c.nome.toLowerCase().includes(codigo.toLowerCase())
    )
    if (!encontrada) {
      toast.error('Caixa não encontrada', { autoClose: 5000 })
      return
    }
    addCaixa(encontrada)
  }

  // validação de PIN (não avança sozinho)
  // dentro de TelaEsterilizacao.jsx, substitua o validarPin inteiro por:
  const validarPin = async () => {
    if (alunoPin.trim().length !== 4) {
      toast.warn('PIN deve ter 4 dígitos', { autoClose: 5000 });
      return;
    }

    try {
      // 1) busca dados do aluno
      const { data: aluno } = await axios.get(`/api/alunos/pin/${alunoPin}`);
      setAlunoNome(aluno.nome);
      setAlunoId(aluno.id);
      setAlunoCodEsterilizacao(aluno.cod_esterilizacao);

      // 2) usa diretamente o campo 'periodo' que já vem na resposta
      //    (antigo res.data.periodo_id estava vindo undefined)
      setAlunoPeriodo(aluno.periodo);
      setAlunoTurno(aluno.turno || '');

      setPinValidated(true);
      toast.success(`PIN validado: ${aluno.nome}`, { autoClose: 5000 });
      fetchHistory();
      avancarButtonRef.current?.focus()
    } catch {
      toast.error('Aluno não encontrado', { autoClose: 5000 });
    }
  };








  // registra entrada ou saída
  const operar = async tipo => {
    if (isSubmitting) return
    if (!pinValidated) {
      toast.warn('Valide o PIN', { autoClose: 5000 })
      return
    }
    if (caixas.length === 0) {
      toast.warn('Adicione ao menos uma caixa', { autoClose: 5000 })
      return
    }
    if (tipo === 'saida' && totalVencidas > 0 && preferSaidaVencidas === null) {
      toast.warn('Escolha se deseja priorizar as caixas vencidas antes de registrar a saída.', { autoClose: 4000 })
      return
    }
    if (tipo === 'saida') {
      const invalidas = caixas.filter(c => {
        // precisa haver pelo menos uma entrada antes
        const h = fullHistory.filter(m => m.caixa_id === c.id && m.tipo === 'entrada')
        return h.length === 0
      })
      if (invalidas.length) {
        invalidas.forEach(c =>
          toast.error(`Caixa "${c.nome}" sem entrada`, { autoClose: 5000 })
        )
        return
      }
    }
    // valida saldo por caixa antes de enviar saída em lote
    if (tipo === 'saida') {
      const semSaldo = uniqueCaixas.filter(c => (stockByBox[c.nome] || 0) < (Number(c.qty) || 1))
      if (semSaldo.length > 0) {
        toast.error('Há caixas sem saldo suficiente para saída. Ajuste as quantidades.', { autoClose: 5000 })
        return
      }
    }
    setIsSubmitting(true)
    try {
      if (tipo === 'saida') {
        // usa batch: agrupa por id e envia quantidade
        const itens = uniqueCaixas.map(c => ({ caixa_id: c.id, quantidade: c.qty }))
        await axios.post('/api/movimentacoes/saida-batch', {
          aluno_pin: alunoPin,
          preferir_vencidas: !!preferSaidaVencidas,
          itens
        })
      } else {
        // entrada mantém fluxo por unidade
        for (const c of caixas) {
          await axios.post('/api/movimentacoes/entrada', { caixa_id: c.id, aluno_pin: alunoPin })
        }
      }
      toast.success(
        `${tipo === 'entrada' ? 'Entrada' : 'Saída'} registrada! ${alunoNome}`,
        { autoClose: 5000 }
      )
      // full reset
      setCaixas([])
      setAlunoPin('')
      setPinValidated(false)
      setOperation(null)
      setAlunoNome('')

      setFullHistory([])
      setPrintedIds([])
      setStep(1)
    } catch {
      toast.error(`Erro ao registrar ${tipo}`, { autoClose: 5000 })
    } finally {
      setIsSubmitting(false)
    }
  }

  // dispara impressão em janela separada
  useEffect(() => {
    if (!printData) return
    const area = document.getElementById('area-impressao')
    if (!area) return
    const html = `
      <html><head><title>Etiqueta</title>
      <style>body{margin:0;padding:8mm;font-family:sans-serif}</style>
      </head><body>${area.innerHTML}</body></html>`
    const w = window.open('', '_blank', 'width=auto,height=auto') //abertura do modal no navegador
    w.document.write(html)
    w.document.close()
    w.focus()
    w.print()
    setPrintedIds(prev => [...prev, printData.caixaId])
    setPrintData(null)
    setTimeout(() => w.close(), 100)
  }, [printData])

  const handlePrint = c => {
    setPrintData({
      alunoId,
      alunoNome,
      periodo: alunoPeriodo || '—',
      caixaId: c.id,
      caixaNome: c.nome,
      criadoEm: c.criado_em || new Date().toISOString(),
      recebidoPor: usuarioLogadoNome,
    });
  };



  // remove TODAS as ocorrências da caixa
  const removerCaixa = id => {
    setCaixas(prev => prev.filter(c => c.id !== id))
    setOrderedBoxIds(prev => prev.filter(x => x !== id))
  }

  const limparTudo = () => {
    setCaixas([])
    setOrderedBoxIds([])
  }

  function setQtyForBox(id, qty) {
    const parsed = Number(qty)
    const desired = Number.isFinite(parsed) ? Math.max(1, Math.floor(parsed)) : 1
    // Referência da caixa pelo id
    const ref = caixas.find(c => c.id === id) || allCaixas.find(c => c.id === id)
    if (!ref) return
    // Limite para saída: não pode exceder estoque disponível
    let limit = desired
    if (operation === 'saida') {
      const available = stockByBox[ref.nome] || 0
      limit = Math.min(desired, Math.max(0, available))
      if (limit === 0) return
    }
    // Recria a lista com a quantidade desejada para este id
    const others = caixas.filter(c => c.id !== id)
    const repeated = Array.from({ length: limit }, () => ref)
    setCaixas([...others, ...repeated])
  }

  function adjustQty(id, delta) {
    const info = uniqueCaixas.find(c => c.id === id)
    if (!info) return
    const current = info.qty || 1
    let next = Math.max(1, current + delta)
    if (operation === 'saida') {
      const available = stockByBox[info.nome] || 0
      next = Math.min(next, Math.max(1, available))
    }
    setQtyForBox(id, next)
  }

  // agrupa e monta lista única na ordem de primeira inserção
  const idToInfo = new Map()
  caixas.forEach((c, idx) => {
    const cur = idToInfo.get(c.id)
    if (cur) {
      cur.qty += 1
      cur.lastIndex = idx
    } else {
      idToInfo.set(c.id, { ...c, qty: 1, lastIndex: idx })
    }
  })
  const uniqueCaixas = orderedBoxIds
    .filter(id => idToInfo.has(id))
    .map(id => idToInfo.get(id))
  const totalSelecionadas = uniqueCaixas.reduce((sum, c) => sum + (Number(c.qty) || 0), 0)

  // calcula estoque atual a partir da API de estoque (com flag vencido)
  const stockByBox = {}
  const vencidoByBoxName = {}
  const vencidasByBoxName = {}
  estoqueRows.forEach(r => {
    const nome = r.caixa_nome
    const saldo = Number(r.saldo) || 0
    if (saldo > 0) {
      stockByBox[nome] = (stockByBox[nome] || 0) + saldo
      if (Number(r.vencido) === 1) vencidoByBoxName[nome] = true
      if (r.vencidas != null) {
        const q = Number(r.vencidas) || 0
        if (q > 0) vencidasByBoxName[nome] = q
      }
    }
  })
  const totalVencidas = (estoqueRows || []).reduce((sum, r) => {
    const saldo = Number(r.saldo) || 0
    const q = Number(r.vencidas) || 0
    return sum + (saldo > 0 ? q : 0)
  }, 0)
  const nomesVencidas = (() => {
    try {
      const arr = (estoqueRows || []).filter(r => (Number(r.saldo) || 0) > 0 && (Number(r.vencidas) || 0) > 0).map(r => r.caixa_nome)
      return arr.slice(0, 6).join(', ')
    } catch { return '' }
  })()
  const totalEstoqueAtual = Object.values(stockByBox).reduce((sum, qty) => sum + (Number(qty) || 0), 0)

  // labels do wizard
  const steps = ['Operação', 'PIN', 'Caixas', 'Impressão']

  // Reseta estado ao voltar para tela de Operação
  function resetToOperation() {
    setOperation(null)
    setAlunoPin('')
    setAlunoNome('')
    setAlunoId('')
    setAlunoPeriodo('')
    setAlunoCodEsterilizacao('')
    setPinValidated(false)
    setFullHistory([])
    setCaixas([])
    setCodigo('')
    setPrintedIds([])
    setPrintData(null)
  }

  function handleBack() {
    const prev = step - 1
    if (prev <= 1) {
      resetToOperation()
      setStep(1)
    } else {
      setStep(prev)
    }
  }

  // Impede avançar do passo 3 (saída) quando faltar estoque
  function canAdvanceFromStep3() {
    if (operation !== 'saida') return true
    const semSaldo = uniqueCaixas.filter(c => (stockByBox[c.nome] || 0) <= 0)
    if (semSaldo.length > 0) {
      const nomes = semSaldo.map(c => c.nome).join(', ')
      toast.error(`Sem estoque para saída: ${nomes}. Ajuste ou remova para continuar.`, { autoClose: 5000 })
      return false
    }
    const excede = uniqueCaixas.filter(c => (Number(c.qty) || 1) > (stockByBox[c.nome] || 0))
    if (excede.length > 0) {
      const nomes = excede.map(c => `${c.nome} (solicitado ${c.qty}, disp. ${stockByBox[c.nome] || 0})`).join('; ')
      toast.error(`Quantidade excede o estoque: ${nomes}. Ajuste para continuar.`, { autoClose: 6000 })
      return false
    }
    return true
  }
  return (
    <div className="max-w-4xl mx-auto py-10 px-4">
      <h2 className="text-2xl font-medium mb-6 text-center">Controle de Esterilização</h2>

      <WizardTimeline steps={steps} step={step} operation={operation} alunoNome={alunoNome} />

      <WizardNav 
        step={step} setStep={setStep} handleBack={handleBack} 
        avancarButtonRef={avancarButtonRef} caixaInputRef={caixaInputRef} 
        registrarButtonRef={registrarButtonRef} caixas={caixas} 
        pinValidated={pinValidated} canAdvanceFromStep3={canAdvanceFromStep3} 
      />

      {/* TELA 1: escolha operação */}
      {step === 1 && (
        <Step1Operacao setOperation={setOperation} setStep={setStep} />
      )}

      {step === 2 && (
        <Step2Pin
          alunoPin={alunoPin} setAlunoPin={setAlunoPin}
          alunoPinInputRef={alunoPinInputRef} validarPin={validarPin}
          pinValidated={pinValidated} alunoNome={alunoNome}
          alunoPeriodo={alunoPeriodo} alunoTurno={alunoTurno}
          alunoCodEsterilizacao={alunoCodEsterilizacao}
          avancarButtonRef={avancarButtonRef}
        />
      )}

      {step === 3 && pinValidated && (
        <Step3Caixas
          operation={operation} totalVencidas={totalVencidas} nomesVencidas={nomesVencidas}
          estoqueRows={estoqueRows} preferSaidaVencidas={preferSaidaVencidas}
          setPreferSaidaVencidas={setPreferSaidaVencidas} uniqueCaixas={uniqueCaixas}
          totalSelecionadas={totalSelecionadas} caixaInputRef={caixaInputRef}
          codigo={codigo} setCodigo={setCodigo} highlightIndex={highlightIndex}
          setHighlightIndex={setHighlightIndex} suggestions={suggestions}
          addCaixa={addCaixa} buscarCaixa={buscarCaixa} avancarButtonRef={avancarButtonRef}
          stockByBox={stockByBox} adjustQty={adjustQty} setQtyForBox={setQtyForBox}
          removerCaixa={removerCaixa} limparTudo={limparTudo} totalEstoqueAtual={totalEstoqueAtual}
          BADGE_STYLES={BADGE_STYLES} vencidasByBoxName={vencidasByBoxName} vencidoByBoxName={vencidoByBoxName}
        />
      )}

      {step === 4 && (
        <Step4Resumo
          uniqueCaixas={uniqueCaixas} handlePrint={handlePrint} printedIds={printedIds}
          operar={operar} operation={operation} registrarButtonRef={registrarButtonRef}
          isSubmitting={isSubmitting}
        />
      )}

      {printData && (
        <div id="area-impressao" style={{ position: 'absolute', left: -10000, top: -10000 }}>
          <Etiqueta
            ref={componentRef} alunoId={printData.alunoId} alunoNome={printData.alunoNome}
            periodo={printData.periodo} caixaNome={printData.caixaNome} caixaId={printData.caixaId}
            criadoEm={printData.criadoEm} recebidoPor={printData.recebidoPor}
          />
        </div>
      )}
    </div>
  )
}
